/**
 * Каталог кабинета, русский. **Язык-источник**: строки пишутся здесь, а `en` и
 * `ar` переводятся с него (план глобала, B5).
 *
 * ─── Как устроен ключ ───────────────────────────────────────────────────────
 *
 * `раздел.имя`, оба через дефис внутри слова: `switcher.other-brands`. Одно
 * исключение и оно содержательное — `error.code.<код>`: часть после `code.` не
 * наша, это машинный код `problem+json` из контракта, и он пишется ровно так,
 * как приходит с сервера. Кабинет ищет строку прямо по нему, без таблицы
 * соответствий: таблица разъехалась бы с контрактом молча.
 *
 * ─── Неразрывные пробелы ────────────────────────────────────────────────────
 *
 * Расставляет их сборка — `app/typo-plugin.ts` ходит по литералам `app/src` и
 * `shared/src`, и каталог для него ничем не отличается от строки, набранной
 * прямо в разметке.
 *
 * Руками здесь набран ровно один — U+00A0 между `#` и словом в счётных
 * строках. Проход его не поставит: правило смотрит на цифру перед пробелом, а
 * там стоит `#`. Без него на экране получилось бы «10 брендов» с обычным
 * пробелом и переносом посреди счёта.
 *
 * ─── Плюрализация ───────────────────────────────────────────────────────────
 *
 * Формы перечислены по CLDR: в русском их три (`one`, `few`, `other`), в
 * английском две, в арабском шесть. Какая подойдёт — решает `Intl.PluralRules`
 * браузера, а не мы. Ветка `other` обязательна везде — это требование ICU.
 */

export const RU = {
  /* ── ошибки API ────────────────────────────────────────────────────────── */

  'error.title': 'Не получилось',
  'error.unknown': 'Что-то не сработало. Повторите ещё раз.',
  'error.ours': 'Что-то не сработало на нашей стороне. Обновите страницу и повторите.',
  'error.field': 'Проверьте это поле.',
  'error.request-id':
    'Если напишете на <mail>{contact}</mail>, укажите номер запроса {requestId} — по нему мы найдём, что случилось.',

  'error.offline.title': 'Нет связи с сервисом',
  'error.offline.detail':
    'Похоже, пропала сеть. Проверьте соединение и повторите — данные на экране остались прежними.',

  'error.unreadable.title': 'Сервис ответил неожиданно',
  'error.unreadable.detail':
    'Мы получили ответ, который не смогли разобрать. Повторите действие. Если повторится, напишите на {contact}, приложив адрес страницы.',

  /* Машинные коды `problem+json`. Показываются, только когда сервер не прислал
     своего `detail`: его формулировка точнее — он знает числа и сроки. */
  'error.code.malformed_json':
    'Запрос не удалось разобрать. Обновите страницу и повторите.',
  'error.code.validation_failed': 'Проверьте заполненные поля — что-то не подходит.',
  'error.code.unauthenticated': 'Нужно войти заново.',
  'error.code.session_expired': 'Сессия истекла. Мы обновим её и повторим запрос.',
  'error.code.invalid_credentials': 'Неверная почта или пароль.',
  'error.code.mfa_required': 'Введите код из приложения-аутентификатора.',
  'error.code.account_locked':
    'Вход временно заблокирован после нескольких неудачных попыток.',
  'error.code.role_forbidden': 'У вашей роли нет прав на это действие.',
  'error.code.plan_feature_required': 'Эта возможность доступна на старших тарифах.',
  'error.code.step_up_required': 'Подтвердите действие паролем или кодом из приложения.',
  'error.code.origin_rejected':
    'Не удалось подтвердить, что действие начато в кабинете. Обновите страницу и повторите.',
  'error.code.captcha_failed':
    'Не получилось подтвердить, что вы человек. Попробуйте ещё раз.',
  'error.code.not_found': 'Ничего не нашлось по этому адресу.',
  'error.code.conflict': 'Сейчас это сделать нельзя: состояние изменилось.',
  'error.code.run_already_active': 'У этого бренда уже идёт такой замер.',
  'error.code.quota_exceeded':
    'Квота расчётного периода кончилась. Докупите объём или перейдите на старший тариф.',
  'error.code.plan_limit_reached':
    'Тариф на это уже не рассчитан. Проверьте квоты на экране «Тариф».',
  'error.code.scan_blocked': 'Замеры остановлены. Проверьте подписку на экране «Тариф».',
  'error.code.last_owner': 'Нельзя убрать последнего владельца организации.',
  'error.code.last_login_method': 'Нельзя снять последний способ входа.',
  'error.code.email_taken': 'Такая почта уже занята.',
  'error.code.idempotency_in_flight':
    'Такой запрос ещё выполняется. Подождите пару секунд.',
  'error.code.idempotency_key_reused':
    'Повторная отправка не совпала с первой. Обновите страницу и повторите.',
  'error.code.gone': 'Ссылка больше не действует.',
  'error.code.payment_required': 'Нет действующей подписки.',
  'error.code.payload_too_large': 'Файл слишком большой.',
  'error.code.unsupported_media_type': 'Такой файл мы не примем.',
  'error.code.rate_limited': 'Слишком часто. Подождите немного и повторите.',
  'error.code.internal': 'На нашей стороне сбой. Мы уже знаем о нём.',
  'error.code.provider_error': 'Сбой у внешнего сервиса. Повторите чуть позже.',
  'error.code.maintenance': 'Идут плановые работы. Скоро вернёмся.',
  'error.code.engine_unavailable':
    'Движок временно недоступен, ответ придёт в следующем замере.',

  /* ── переключатель контекста ───────────────────────────────────────────── */

  'switcher.trigger.brand': 'Бренд',
  'switcher.trigger.brand-and-client': 'Бренд и клиент',
  'switcher.organizations': 'Организация',
  'switcher.other-brands': 'Остальные бренды',
  'switcher.empty': 'Брендов пока нет — заведите первый в разделе «Бренды»',
  'switcher.no-domain': 'Добавьте сайт в карточке бренда',
  'switcher.search.brands': 'Поиск по брендам',
  'switcher.search.brands-and-clients': 'Поиск по бренду и клиенту',
  'switcher.pick': 'Выберите бренд',
  'switcher.switch-failed': 'Не удалось переключить организацию. Повторите ещё раз.',
  'switcher.brand-count':
    '{count, plural, one {# бренд} few {# бренда} other {# брендов}}',

  /* ── стадии воронки ────────────────────────────────────────────────────── */

  'funnel.awareness': 'Узнавание',
  'funnel.consideration': 'Выбор',
  'funnel.comparison': 'Сравнение',
  'funnel.decision': 'Решение',
  'funnel.reputation': 'Репутация',

  'funnel.awareness.note': 'Ещё разбирается в категории',
  'funnel.consideration.note': 'Присматривается к вариантам',
  'funnel.comparison.note': 'Сравнивает конкретные бренды',
  'funnel.decision.note': 'Готов купить и ищет у кого',
  'funnel.reputation.note': 'Проверяет, что о вас говорят',

  /* ── «не измерено» ─────────────────────────────────────────────────────────
     Слово продукта (data-viz.md §3): никогда не ноль, не пустая ячейка и не
     `n/a`. Вторая форма — для середины фразы и подписи клетки. Константа
     `NOT_MEASURED` в `shared/src/format.ts` остаётся для витрины и экранов,
     ещё не переехавших на каталоги (волны 2–3). */

  'not-measured': 'Не измерено',
  'not-measured.inline': 'не измерено',

  /* ── разделы кабинета ──────────────────────────────────────────────────── */

  'section-group.scan': 'Замер',
  'section-group.actions': 'Что делать',
  'section-group.organization': 'Организация',

  'section.overview': 'Обзор бренда',
  'section.portfolio': 'Портфель',
  'section.brands': 'Бренды',
  'section.attention': 'Требует внимания',
  'section.engines': 'Движки',
  'section.visibility': 'Видимость по вопросам',
  'section.history': 'Динамика',
  'section.competitors': 'Конкуренты',
  'section.questions': 'Семантическое ядро',
  'section.runs': 'Прогоны',
  'section.recommendations': 'Рекомендации',
  'section.plans': 'Планы действий',
  'section.sources': 'Источники',
  'section.alerts': 'Оповещения',
  'section.reports': 'Центр отчётов',
  'section.workspaces': 'Пространства',
  'section.members': 'Участники',
  'section.clients': 'Клиенты',
  'section.plan': 'Тариф',
  'section.activity': 'Журнал действий',
  'section.settings': 'Настройки',
  'section.ownership-transfers': 'Приём владения',
  'section.audits-claim': 'Присвоение аудита',
  'section.not-built': 'Раздел в работе',

  /* ── подписи к перечислениям контракта (ui/labels.ts) ─────────────────────
     Часть ключа после последней точки — значение перечисления из контракта,
     пишется ровно так, как приходит с сервера (то же правило, что у
     `error.code.<код>`). */

  'label.name-source.manual': 'Название задали вы',
  'label.name-source.domain': 'Пока по адресу сайта',
  'label.name-source.site': 'Прочитали на сайте',

  'label.alias-kind.primary': 'Основное название',
  'label.alias-kind.legal': 'Юридическое лицо',
  'label.alias-kind.translit': 'Латиницей',
  'label.alias-kind.short': 'Сокращение',
  'label.alias-kind.misspelling': 'Частая опечатка',
  'label.alias-kind.domain': 'Домен',
  'label.alias-kind.parent': 'Головная сеть',

  'label.parent-alias-note':
    'Головная сеть — имя, под которым покупателю продают эту точку. Засчитывать её упоминание бренду разрешает отрасль: у дилеров и клиник — да, там, где вывеска владельца ничего не говорит покупателю, — нет.',

  'label.sentiment.positive': 'положительная',
  'label.sentiment.neutral': 'нейтральная',
  'label.sentiment.negative': 'отрицательная',

  'label.parser.llm': 'прочитан нейросетью',
  'label.parser.text_match': 'найден поиском вхождения',

  'label.question-source.generated': 'Придуман',
  'label.question-source.manual': 'Вручную',
  'label.question-source.pack': 'Из пакета',
  'label.question-source.imported': 'Загружено файлом',
  'label.question-source.audit': 'Из аудита',

  'label.competitor-source.detected': 'Найден в ответах',
  'label.competitor-source.manual': 'Добавлен вручную',
  'label.competitor-source.vertical_default': 'Из типового пакета',

  'label.run-kind.weekly': 'Недельная перепись',
  'label.run-kind.pulse': 'Ежедневный пульс',
  'label.run-kind.manual': 'Ручной замер',
  'label.run-kind.repair': 'Заполнение пропусков',
  'label.run-kind.memory': 'Проверка памяти движков',

  'label.memory-verdict.known_and_found': 'Знают и называют',
  'label.memory-verdict.known_not_found': 'Знают, но не называют',
  'label.memory-verdict.found_via_search': 'Называют, но не знают',
  'label.memory-verdict.absent': 'Не знают и не называют',

  'label.memory-verdict-note.known_and_found': 'Называет бренд и с поиском, и без него',
  'label.memory-verdict-note.known_not_found':
    'Без поиска называет, с поиском — нет: работа со страницами',
  'label.memory-verdict-note.found_via_search':
    'С поиском называет, без поиска — нет: держится на страницах',
  'label.memory-verdict-note.absent': 'Не называет ни с поиском, ни без него',

  'label.report-kind.weekly': 'Недельный',
  'label.report-kind.monthly': 'Месячный',
  'label.report-kind.custom': 'Произвольный период',
  'label.report-kind.agency': 'Для клиента агентства',
  'label.report-kind.audit_carryover': 'Из бесплатного аудита',

  'label.report-kind-title.weekly': 'Недельный отчёт',
  'label.report-kind-title.monthly': 'Месячный отчёт',
  'label.report-kind-title.custom': 'Отчёт за произвольный период',
  'label.report-kind-title.agency': 'Отчёт для клиента агентства',
  'label.report-kind-title.audit_carryover': 'Отчёт из бесплатного аудита',

  'label.series-basis.weekly': 'недельная перепись',
  'label.series-basis.pulse': 'ежедневный пульс',

  'label.run-state.queued': 'В очереди',
  'label.run-state.running': 'Идёт',
  'label.run-state.completed': 'Завершён',
  'label.run-state.partial': 'Сетка неполная',
  'label.run-state.failed': 'Сорвался',
  'label.run-state.cancelled': 'Остановлен',

  'label.brand-status.active': 'Замеряется',
  'label.brand-status.paused': 'На паузе',
  'label.brand-status.archived': 'В архиве',

  'label.timeline.site_change': 'сайт',
  'label.timeline.engine_model_change': 'нейросеть движка',
  'label.timeline.engine_search_change': 'веб-поиск движка',
  'label.timeline.question_set_change': 'ядро',
  'label.timeline.recommendation_done': 'рекомендация',
  'label.timeline.plan_change': 'тариф',
  'label.timeline.manual_note': 'отметка',

  /* ── шесть состояний экрана (ui/screen-states.tsx) ────────────────────── */

  'state.scan-blocked-fallback':
    'Пока подписка не активна, новые ответы движков не собираются.',
  'state.pick-brand': 'Выберите бренд в переключателе наверху.',

  'state.step.site': 'Разбираем сайт',
  'state.step.questions': 'Собираем вопросы',
  'state.step.engines': 'Опрашиваем движки',
  'state.step.count': 'Считаем',

  'state.never.eyebrow': 'Замера ещё не было',
  'state.never.title': 'Первого замера ещё не было',
  'state.never.title-named': '{brandName} мы пока не спрашивали',
  'state.never.body':
    'Мы соберём вопросы, которые ваши покупатели задают нейросетям, и опросим движки. Первый замер занимает несколько минут — после него на этом экране появится индекс ИИ-видимости и разбор по движкам.',

  'state.measuring.queued': 'Замер поставлен в очередь',
  'state.measuring.done':
    'выполнено {done, number} из {planned, plural, one {# задачи} other {# задач}}',
  'state.measuring.engines':
    'закрыто {done, number} из {total, plural, one {# движка} other {# движков}}',
  'state.measuring.failures':
    '{failed, plural, one {# сбой} few {# сбоя} other {# сбоев}}',
  'state.measuring.cost-capped':
    'Замер упёрся в наш предел расходов — часть клеток сетки останется незаполненной. Из вашей квоты это ничего не берёт.',

  'state.partial.title': 'Замер выполнен не полностью',
  'state.partial.closed':
    'Закрыто {done, number} из {planned, plural, one {# задачи} other {# задач}}{failed, plural, =0 {} other {, ещё # — с ошибкой}}. Числа ниже посчитаны по тому, что собрано, — они верны, но охватывают не всю сетку.',
  'state.partial.fallback':
    'Часть задач замера не выполнилась. Числа ниже посчитаны по собранному.',
  'state.partial.broken':
    'Техническое: {engines} — {count, plural, =1 {движок отвечал ошибками и был отключён} other {движки отвечали ошибками и были отключены}} до конца замера. Это сбой источника, а не падение бренда: ответы придут следующим прогоном.',

  'state.no-data.eyebrow': 'За этот период',
  'state.no-data.title': 'Замеров в выбранные дни не было',
  'state.no-data.body':
    '{period} — за эти дни ни одной завершённой сетки не закрылось, поэтому показывать нечего.',
  'state.no-data.history':
    'Ваш тариф хранит историю {months, plural, one {# месяц} few {# месяца} other {# месяцев}}: точки старше просто не хранятся.',
  'state.no-data.tail': 'Выберите период поближе или запустите замер.',

  'state.quota.never.eyebrow': 'Замер ещё не начинался',
  'state.quota.never.title': 'Тариф откроет вопросы для отслеживания',
  'state.quota.never.lead':
    'Пока тариф не оплачен, вопросы не отслеживаются и движки не опрашиваются.',
  'state.quota.never.can-pay':
    'Выберите тариф — вопросы соберутся, и первый замер можно будет запустить сразу, не дожидаясь недельного.',
  'state.quota.never.cannot-pay':
    'Тариф выбирает владелец организации. После оплаты вопросы соберутся, а замеры пойдут каждую неделю.',

  'state.quota.eyebrow-blocked': 'Замеры остановлены',
  'state.quota.eyebrow-exhausted': 'Квота израсходована',
  'state.quota.title-blocked': 'Новые замеры сейчас не запускаются',
  'state.quota.title-exhausted': 'Квота расчётного периода кончилась',
  'state.quota.tracked':
    'Отслеживается {used, number} из {total, plural, one {# вопроса} other {# вопросов}} тарифа.',
  'state.quota.over-since': 'Перерасход держится с {date}.',
  'state.quota.kept': 'Уже собранные числа остаются на экране и никуда не денутся.',
  'state.quota.owner-only': 'Изменить тариф может владелец организации.',

  'state.blocked-reason.payment_failed':
    'Платёж за подписку не прошёл — оплатите на экране «Тариф».',
  'state.blocked-reason.subscription_past_due':
    'Платёж за подписку просрочен — оплатите на экране «Тариф».',
  'state.blocked-reason.subscription_expired':
    'Подписка закончилась — продлите её на экране «Тариф».',
  'state.blocked-reason.subscription_cancelled':
    'Подписка отменена — вернуть её можно на экране «Тариф».',
  'state.blocked-reason.quota_exhausted': 'Вопросы тарифа на этот период закончились.',
  'state.blocked-reason.fallback':
    'Пока квота не обновится или тариф не изменится, новые ответы движков не собираются.',

  'state.readonly':
    'Этот кабинет открыт вам на просмотр и выгрузку. Изменения — на стороне тех, кто ведёт замер.',

  /* ── обвязка экрана (ui/screen.tsx) ───────────────────────────────────── */

  'screen.pick.eyebrow': 'Выберите бренд',
  'screen.pick.title': 'Какой бренд смотрим',
  'screen.pick.body': 'Раздел показывает числа одного бренда.',
  'screen.new-brand': 'Завести бренд',

  /* ── основание экрана (ui/basis.ts) ───────────────────────────────────── */

  'basis.data-for': 'Данные за {date}',
  'basis.engines-answered':
    '{answered, plural, one {# движок} few {# движка} other {# движков}} из {total, plural, one {# подключённого} other {# подключённых}} ответили',
  'basis.grid':
    '{filled, number} из {planned, plural, one {# клетки} other {# клеток}} сетки',
  'basis.grid-unknown': 'полнота сетки неизвестна',
  'basis.grid-incomplete': 'сетка неполная',
  'basis.no-runs': 'Завершённых замеров пока нет',
  'basis.run-dated': 'Замер за {date}',
  'basis.run-latest': 'Последний завершённый замер',
  'basis.per-cell':
    '{n, plural, one {# прогон} few {# прогона} other {# прогонов}} на клетку',
  'basis.shown-of':
    'показано {shown} {shown, plural, one {вопрос} few {вопроса} other {вопросов}} из {measured}',
  'basis.count': '{measured, plural, one {# вопрос} few {# вопроса} other {# вопросов}}',
  'basis.read-incomplete': 'замер прочитан не полностью',

  /* ── подтверждение на действии (ui/StepUp.tsx) ────────────────────────────
     Ключи `step-up.action.<код>` обязаны СЛОВО В СЛОВО повторять реестр
     сервера (`stepup_registry.py`) — сверяет `tools/check-step-up.mjs`. */

  'step-up.action.mfa.disable': 'отключение второго шага при входе',
  'step-up.action.mfa.recovery_codes': 'выпуск запасных кодов на случай потери телефона',
  'step-up.action.account.delete': 'удаление учётной записи',
  'step-up.action.account.email_change': 'смену адреса почты',
  'step-up.action.whitelabel.update': 'настройку отчётов под своим брендом',
  'step-up.action.ownership.transfer': 'передачу владения организацией',
  'step-up.action.ownership.accept': 'приём владения организацией',
  'step-up.action.members.update': 'смену роли участника',
  'step-up.action.members.remove': 'отзыв доступа',
  'step-up.action.members.invite': 'приглашение участника',
  'step-up.action.portal.create': 'создание портала клиента',
  'step-up.action.portal.delete': 'удаление портала клиента',
  'step-up.action.billing.cancel_subscription': 'отмену подписки',
  'step-up.action.billing.delete_payment_method': 'удаление способа оплаты',
  'step-up.action.api_token.create': 'выпуск ключа доступа к данным по API',

  'step-up.title': 'Подтвердите действие',
  'step-up.cancel': 'Отмена',
  'step-up.confirm': 'Подтвердить',
  'step-up.checking': 'Проверяем…',
  'step-up.action-generic': 'действие',
  'step-up.lead':
    'Мы просим подтвердить {what}: это меняет доступ или деньги. Подтверждение действует один раз — на следующее такое действие спросим снова.',
  'step-up.open-settings': 'Открыть настройки учётной записи',
  'step-up.secret.label-totp': 'Пароль или код из приложения',
  'step-up.secret.label': 'Пароль',
  'step-up.secret.hint-totp': 'Шесть цифр из приложения-аутентификатора или ваш пароль',
  'step-up.secret.hint': 'Тот же пароль, которым вы входите',

  /* ── клетка матрицы (ui/CellCard.tsx) ─────────────────────────────────── */

  'cell.word.named': 'назван',
  'cell.word.not-named': 'не назван',
  'cell.word.named-one-pending': 'назван, прогон один из двух',
  'cell.word.not-named-one-pending': 'не назван, прогон один из двух',
  'cell.word.both': 'назван в обоих',
  'cell.word.one-of-two': 'назван в одном из двух',

  'cell.try.not-named': 'Не назван',
  'cell.try.named': 'Назван',
  'cell.try.no-position': 'место не определено',
  'cell.try.position': 'место {n}',
  'cell.try.sentiment': 'тональность {word}',

  'cell.title': 'Вопрос и движок',
  'cell.close': 'Закрыть',
  'cell.run-dated': 'замер за {date}',
  'cell.run-latest': 'последний завершённый замер',
  'cell.empty':
    'Разобранного ответа движка по этому вопросу в этом замере нет, поэтому сравнивать нечего. Клетка говорит не о том, что нас не назвали, а о том, что ответа нет.',
  'cell.cut': 'Замер прочитан не полностью: часть ответов могла не доехать до экрана.',
  'cell.partial':
    'Прогонов в клетке {n} из {total}, и согласия прогонов в такой клетке нет.',
  'cell.attempt.unknown': 'Прогон, номер неизвестен',
  'cell.attempt.single': 'Прогон',
  'cell.attempt.of': 'Прогон {no} из {total}',
  'cell.parser.text-match-found':
    'Ответ {kind}: имя найдено, место в списке не ставится.',
  'cell.parser.text-match-not-found':
    'Ответ {kind}: имя не найдено, место в списке такой разбор не ставит вовсе.',
  'cell.model': 'Нейросеть {model}',
  'cell.full-answer': 'Показать ответ целиком',
  'cell.retention':
    'Текста ответа уже нет: он вычищен по сроку хранения тарифа раньше метаданных.',
  'cell.empty-answer': 'Ответ пустой: движок вернул текст без единого знака.',

  /* ── «знают или находят» (ui/Memory.tsx) ──────────────────────────────── */

  'memory.title': 'Знают или находят',
  'memory.lede.pending-probe':
    'Движки отвечают на те же вопросы с выключенным поиском. Когда закончат, здесь будет видно, знают вас движки или только находят.',
  'memory.lede.pending':
    'Мы задаём движкам те же вопросы с выключенным поиском. Проверка идёт после недельного замера — тогда здесь появится, знают вас движки или только находят.',
  'memory.probe-off': 'Не измерено · поиск выключается у {engines}',
  'memory.lede':
    'Один и тот же вопрос задан дважды: с поиском и без него. Если бренд называют только с поиском, движок его не помнит — и это другая работа, чем «нас не называют».',
  'memory.caption': 'Вопросы, заданные с поиском и без него · под замером {date}',
  'memory.head': 'Что показала проверка',
  'memory.row.share': 'Знают без поиска',
  'memory.row.share-sub': 'Доля вопросов, где движок называет бренд по памяти',
  'memory.row.compared': 'Сравнено вопросов',
  'memory.row.compared-sub': 'Сколько вопросов сравнили из закрытой сетки замера',
  'memory.foot.none': 'Новых вердиктов не будет',
  'memory.foot.verdicts':
    'Вердикты по {counted, number} из {whole, plural, one {# движка} other {# движков}}',
  'memory.foot.retired': ' · после замера отключили {engines}',
  'memory.foot.moving': ' · сетка закрыта не целиком: доли ещё сдвинутся',
  'memory.foot.incomplete': ' · диагноз неполный',
  'memory.open-questions': 'Разобрать по вопросам',
  'memory.probe.queued': 'Проверка памяти в очереди',
  'memory.probe.running':
    'Проверка памяти идёт: опрошено {done, number} из {planned, plural, one {# клетки} other {# клеток}}',
  'memory.dialog.title': 'Вердикты по вопросам',
  'memory.dialog.lead':
    'Какие именно вопросы движок знает по памяти, а какие называет только с поиском.',
  'memory.filter.engine': 'Движок',
  'memory.filter.verdict': 'Вердикт',
  'memory.filter.all-engines': 'Все движки',
  'memory.filter.all-verdicts': 'Все вердикты',
  'memory.empty.engine':
    '{engine} в этом замере не сравнил ни одного вопроса: вердиктов у него нет. Выберите другой движок.',
  'memory.empty.verdict':
    'Под выбранный вердикт не попал ни один вопрос. Снимите фильтр — и увидите все сравнённые вопросы этого замера.',
  'memory.table.caption': 'Вердикты под замером {date}',
  'memory.col.question': 'Вопрос',
  'memory.col.engine': 'Движок',
  'memory.col.verdict': 'Вердикт',
  'memory.shown':
    'Показано {shown, number} из {total, plural, one {# сравнения} other {# сравнений}}',
  'memory.more': 'Показать ещё',
  'memory.reading': 'Читаем…',

  /* Союз перечисления: «ChatGPT, Gemini и DeepSeek». Запятая остаётся в коде —
     она одинаковая, а союз у языков разный. */
  'list.and': ' и ',

  /* ── частота замера (ui/cadence.tsx) ──────────────────────────────────── */

  'cadence.weekly': 'Раз в неделю',
  'cadence.monthly': 'Раз в месяц',
  'cadence.legend': 'Частота замера',
  'cadence.day-label': 'Число месяца',
  'cadence.day-option': '{day}-е число',
  'cadence.short-month': 'В коротком месяце замер встанет на последний день',
  'cadence.readonly': 'Частота замера: {value} · {next}',
  'cadence.value.weekly': 'раз в неделю',
  'cadence.value.monthly': 'раз в месяц',
  'cadence.next.paused': 'Замер на паузе',
  'cadence.next.disabled': 'Замер по расписанию выключен',
  'cadence.next.asap': 'Ближайший замер: в ближайший запуск',
  'cadence.next.at': 'Ближайший замер: {when} МСК',

  /* ── показ данных (ui/viz.tsx) ────────────────────────────────────────── */

  'viz.no-compare': 'Не с чем сравнить',
  'viz.series-title': 'Ряд по дням: {names}',
  'viz.empty': 'Точек за этот период нет. Линия появится после первой полной сетки.',
  'viz.show-data': 'Показать данные',
  'viz.col.date': 'Дата',
  'viz.col.what': 'Что случилось',
  'viz.col.question': 'Вопрос',
  'viz.marks-prefix': 'Отметки к ряду:',
  'viz.mult.head':
    'Все графики в одной шкале: от 0 до {top}{unit}. В ряду {points, plural, one {# точка} few {# точки} other {# точек}}, с <tnum>{from}</tnum> по <tnum>{to}</tnum>. Штриховкой отмечены дни, когда движок не измерялся.',
  'viz.mult.none': 'Ни одного замера за период',
  'viz.mult.single': 'Одна точка — ряд ещё не построен',
  'viz.mult.by-engines': 'Ряд по движкам: {names}',

  'viz.cell.both': 'назван в обоих прогонах',
  'viz.cell.one': 'назван в одном прогоне из двух',
  'viz.cell.none': 'не назван',
  'viz.cell-short.both': 'в обоих',
  'viz.cell-short.one': 'в одном',
  'viz.cell-short.none': 'не назван',

  /* ── подготовка набора вопросов (ui/preparation.tsx) ──────────────────── */

  'prepare.body':
    'Готовим вопросы: читаем сайт и пишем набор под ваш бизнес{planned, plural, =0 {} one { — до # вопроса} other { — до # вопросов}}. Обычно это занимает {duration}. Страницу можно закрыть: набор пишется на сервере.',
  'prepare.failed': 'Вопросы не собрались',
  'prepare.retry': 'Собрать заново',
  'prepare.retrying': 'Собираем…',
  'prepare.note-fallback':
    'Сайт не удалось разобрать настолько, чтобы написать вопросы покупателя. Проверьте адрес сайта в карточке бренда и попробуйте ещё раз.',

  /* ── выпадающее меню (ui/Menu.tsx) ────────────────────────────────────── */

  'menu.empty': 'Здесь пока пусто',
  'menu.no-results': 'По запросу «{query}» ничего нет',

  /* ── контролы экранов (ui/controls.tsx) ───────────────────────────────── */

  'control.period': 'Период',
  'control.period.week': '7 дней',
  'control.period.month': '30 дней',
  'control.period.quarter': 'Квартал',
} as const;
